/* eslint-disable @typescript-eslint/no-explicit-any */
import classNames from 'classnames'
import type { FC } from 'react'
import React, { forwardRef, useMemo } from 'react'

import { useRadioGroupContext } from './radio-group-context'
import type {
  RadioComponents,
  RadioProps,
  RadioStatefulThemeProps
} from './types'
import { useRadio } from './use-radio'
import type { Theme } from '../../theme-provider/types'
import { useSafeThemeUI } from '../../theme-provider/use-theme-ui'
import { callAll } from '../../utils/function'
import { EMPTY_OBJECT, get, pick } from '../../utils/object'
import {
  getLayout,
  getMargin,
  omitLayout,
  omitMargin
} from '../../utils/props-utils'
import { resolveSize } from '../../utils/size-utils'
import { formikField } from '../_adaptors/fields'
import { InputBase } from '../_primitives/input/input'
import type { InputProps } from '../_primitives/input/types'
import Label from '../_primitives/label/label'
import Text from '../_primitives/text/text'

const RadioInputBase = React.forwardRef<
  HTMLInputElement,
  InputProps & RadioStatefulThemeProps
>(function RadioInputBase(props, ref) {
  // @ts-ignore
  return <InputBase ref={ref} {...props} />
})

export type RadioPropsPrivate = RadioProps & { theme?: Theme }

/**
 * Radio component is used in forms when a user needs to select a single value from
 * several options.
 *
 */
// @ts-ignore
const Radio: FC<RadioPropsPrivate> & RadioComponents = forwardRef<
  HTMLInputElement,
  RadioPropsPrivate
>(function Radio(props, ref) {
  const {
    variant,
    readOnly,
    disabled = readOnly,
    className,
    state: stateProps,
    size,
    spacing = 'sm',
    isInvalid,
    disableColor,
    disableBg,
    showHoverShadow,
    hoverShadowColor,
    radioBorder,
    radioInnerSize,
    radioInnerColor,
    children,
    labelProps = EMPTY_OBJECT,
    active,
    hover,
    canDeselect,
    ...rest
  } = props
  // props not extracted from props (i.e. need to be in rest when it is past to useRadio hook)
  const { onChange: onChangeProp, value: valueProp } = props
  const theme = useSafeThemeUI(props)
  const fontSize = size
    ? resolveSize(size, get(theme, 'sizes.font'))
    : undefined
  const state = isInvalid ? 'danger' : stateProps
  const group = useRadioGroupContext()

  let isChecked = props.checked
  if (group?.value != null && valueProp != null) {
    isChecked = group.value === valueProp
  }

  let onChange = onChangeProp
  if (group?.onChange && valueProp != null) {
    onChange = callAll(group.onChange, onChangeProp)
  }

  const name = props?.name ?? group?.name

  const { getInputProps, getCheckboxProps, getLabelProps, htmlProps } =
    useRadio({
      ...rest,
      isReadOnly: readOnly,
      isDisabled: disabled,
      canDeselect: canDeselect ?? group?.canDeselect,
      isChecked,
      onChange,
      name
    })

  const layoutProps = {
    ...getMargin(htmlProps as any),
    ...getLayout(htmlProps as any)
  }
  const otherProps = omitMargin(omitLayout(htmlProps as any))

  const checkboxProps = getCheckboxProps(otherProps)
  const inputProps = getInputProps(
    {
      onMouseDown: rest.onMouseDown,
      onMouseUp: rest.onMouseUp
    },
    ref
  )

  const { rootStyles, checkboxStyles, labelStyles } = useMemo(
    () => ({
      rootStyles: {
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'top',
        cursor: readOnly ? 'auto' : disabled ? 'not-allowed' : 'pointer',
        minWidth: '0'
      },
      checkboxStyles: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      },
      labelStyles: {
        userSelect: 'none',
        ml: spacing
      }
    }),
    [disabled, spacing, readOnly]
  )

  return (
    <Label
      className="csg-tui-radio"
      fontSize={fontSize}
      ellipsis={false}
      {...layoutProps}
      __css={rootStyles}
    >
      <RadioInputBase
        className={classNames('csg-tui-radio-input', className, {
          'is-active': active,
          'is-hover': hover,
          'is-read-only': readOnly
        })}
        variant={variant ? `radio.${variant}` : 'radio.default'}
        state={state}
        disableBg={disableBg}
        disableColor={disableColor}
        showHoverShadow={showHoverShadow}
        hoverShadowColor={hoverShadowColor}
        radioBorder={radioBorder}
        radioInnerSize={radioInnerSize}
        radioInnerColor={radioInnerColor}
        {...inputProps}
      />
      <Text
        className="csg-tui-radio-control"
        {...{ ...checkboxProps, ...checkboxStyles }}
      />
      {children && (
        <Text
          className="csg-tui-radio-label"
          ellipsis
          {...{
            ...getLabelProps(labelProps),
            ...labelStyles,
            ...pick(inputProps, ['onMouseDown', 'onMouseUp'])
          }}
        >
          {children}
        </Text>
      )}
    </Label>
  )
})

Radio.Formik = formikField(Radio, { isCheckbox: true })

export default Radio
